<template>
  <div class="min-h-screen bg-bochas-bg">
    <ImageBackgroundSection
      seed="resultados-hero"
      :image-url="heroImageUrl"
      :loading-image="heroImageLoading"
      alt="Resultados"
      :title="heroMedia?.title || 'Resultados'"
      :subtitle="
        heroMedia?.subtitle ||
        'Consultá los resultados oficiales y seguí el desempeño de cada competencia'
      "
    />

    <!-- Loading -->
    <div v-if="error && torneos.length" class="max-w-7xl mx-auto px-6 pt-6 text-sm text-bochas-text-muted" role="status">
      No pudimos actualizar los resultados. Seguís viendo los últimos datos cargados.
      <button type="button" class="ml-2 underline" :disabled="loading" @click="load">Reintentar</button>
    </div>

    <section v-if="loading && !torneos.length" class="max-w-7xl mx-auto px-6 py-12 text-center">
      <div
        class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </section>

    <!-- Error -->
    <section v-else-if="error && !torneos.length" class="max-w-7xl mx-auto px-6 py-12">
      <HomeSectionState state="error" :error-message="error" @retry="load" />
    </section>

    <!-- Empty: no hay torneos cargados en absoluto -->
    <section
      v-else-if="torneos.length === 0"
      class="max-w-7xl mx-auto px-6 py-12 text-center"
    >
      <p class="text-gray-400 text-base sm:text-lg">
        Aún no hay resultados cargados. Volvé pronto.
      </p>
    </section>

    <!-- ============ RESULTADOS ============ -->
    <template v-else>
      <!-- Filtros -->
      <section
        class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:pt-24 lg:pb-0 sm:py-12"
      >
        <div class="section-header">
          <div class="section-header__icon">
            <IconFbochas name="trophy" :size="32" color="#fff" />
          </div>
          <span class="section-header__label">Competencia</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-8">
          <div class="relative">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              :size="18"
            />
            <input
              v-model="filterAssociation"
              type="text"
              placeholder="Buscar por Asociación"
              class="w-full px-12 py-4 bg-bochas-card-bg border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-bochas-azul"
            />
          </div>
          <div class="relative">
            <Calendar
              class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              :size="18"
            />
            <input
              v-model="filterDate"
              type="date"
              class="w-full px-12 py-4 bg-bochas-card-bg border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-bochas-azul"
            />
          </div>
          <div class="relative">
            <Tag
              class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              :size="18"
            />
            <input
              v-model="filterCategory"
              type="text"
              placeholder="Categoría"
              class="w-full px-12 py-4 bg-bochas-card-bg border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-bochas-azul"
            />
          </div>
        </div>
      </section>

      <!-- Empty: filtros sin resultados -->
      <section
        v-if="groupedTorneos.length === 0"
        class="max-w-7xl mx-auto py-16 px-6 sm:px-6 lg:px-0 lg:py-24 sm:py-12"
      >
        <p class="text-gray-400 text-base sm:text-lg">
          No se encontraron resultados con los filtros aplicados.
        </p>
      </section>

      <section
        v-else
        class="max-w-7xl mx-auto py-16 px-6 sm:px-6 lg:px-0 lg:pt-12 lg:pb-24 sm:py-12"
      >
        <ResultadosAccordion
          :groups="paginatedGroups"
          @open-gallery="openGallery"
        />

        <!-- Paginación -->
        <nav
          v-if="totalPages > 1"
          class="flex justify-center items-center gap-2 mt-14 sm:mt-10"
          aria-label="Paginación"
        >
          <button
            type="button"
            @click="prevPage"
            :disabled="currentPage === 1"
            class="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Página anterior"
          >
            <ChevronLeft :size="20" />
          </button>

          <button
            v-for="(page, pIdx) in visiblePages"
            :key="`${page}-${pIdx}`"
            type="button"
            @click="goToPage(page)"
            :disabled="page === '...'"
            :class="[
              'min-w-10 h-10 px-3 flex items-center justify-center rounded-full font-semibold transition-colors',
              page === currentPage
                ? 'bg-bochas-rojo text-white'
                : 'bg-white/10 text-white hover:bg-white/20',
              page === '...' ? 'cursor-default hover:bg-white/10' : '',
            ]"
          >
            {{ page }}
          </button>

          <button
            type="button"
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Página siguiente"
          >
            <ChevronRight :size="20" />
          </button>
        </nav>
      </section>
    </template>

    <ResultadosGallery
      :open="galleryOpen"
      :images="galleryImages"
      :index="galleryIndex"
      @update:index="galleryIndex = $event"
      @close="closeGallery"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useListingState } from "@/composables/useListingState";
import HomeSectionState from "@/components/Home/HomeSectionState.vue";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Search,
  Tag,
} from "lucide-vue-next";
import { listarTorneos } from "@/services/tournaments/tournamentsService";
import ResultadosAccordion from "@/components/Resultados/ResultadosAccordion.vue";
import ResultadosGallery from "@/components/Resultados/ResultadosGallery.vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import ImageBackgroundSection from "@/components/Common/ImageBackgroundSection.vue";
import { useSiteMediaImageState } from "@/composables/useSiteMediaImage";

const GROUPS_PER_PAGE = 3;
const {
  imageUrl: heroImageUrl,
  item: heroMedia,
  loading: heroImageLoading,
} = useSiteMediaImageState("resultados.hero");

const torneos = ref([]);
const loading = ref(true);
const error = ref("");

// === Filtros ===
const { filterAssociation, filterDate, filterCategory, currentPage } = useListingState("resultados", {
  filterAssociation: "",
  filterDate: "",
  filterCategory: "",
  currentPage: 1,
});

// === Paginación ===

function prevPage() {
  if (currentPage.value > 1) currentPage.value--;
}
function nextPage() {
  if (currentPage.value < totalPages.value) currentPage.value++;
}
function goToPage(page) {
  if (page === "...") return;
  currentPage.value = page;
}

// === Galería (lightbox) ===
const galleryOpen = ref(false);
const galleryImages = ref([]);
const galleryIndex = ref(0);

function openGallery(torneo) {
  galleryImages.value = (torneo.images || []).map((i) => i.url);
  galleryIndex.value = 0;
  galleryOpen.value = true;
  document.body.style.overflow = "hidden";
}
function closeGallery() {
  galleryOpen.value = false;
  document.body.style.overflow = "";
}

// === Helpers ===
function normalizeDate(d) {
  return String(d || "").split("T")[0];
}

function weekendGroupKey(item) {
  const values = (item.dates || [])
    .map((date) => date.event_date || date)
    .filter(Boolean)
    .sort();
  const source = values[0] || item.event_date;
  const [y, m, d] = normalizeDate(source)
    .split("-")
    .map((n) => parseInt(n, 10));
  if (!y || !m || !d) return normalizeDate(item.event_date);
  const date = new Date(y, m - 1, d);
  const dow = date.getDay();
  const start = dow === 5 ? date : new Date(y, m - 1, d - ((dow + 2) % 7));
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(start.getDate()).padStart(2, "0")}`;
}

const flatItems = computed(() => {
  const out = [];
  for (const t of filteredTorneos.value) {
    const catsWithPodium = (t.categories || [])
      .filter((cat) => (cat.podiums || []).length > 0)
      .sort((a, b) => {
        if (a.display_order === 999 && b.display_order !== 999) return -1;
        if (a.display_order !== 999 && b.display_order === 999) return 1;
        return (a.display_order || 0) - (b.display_order || 0);
      });
    if (catsWithPodium.length === 0) {
      out.push({
        id: t.id,
        tournamentId: t.id,
        tournamentUuid: t.uuid,
        event_date: t.event_date,
        name: t.name,
        categoryId: null,
        categoryName: "",
        categoryCity: "",
        categoryDisplayOrder: null,
        association: t.association,
        location: t.location,
        description: t.description,
        date_range: t.date_range || "",
        weekend_label: t.weekend_label || "",
        dates: t.dates || [],
        images: t.images || [],
        cover_image: t.cover_image,
        isFinal: false,
        categories: [],
      });
      continue;
    }
    const final = catsWithPodium.find((cat) => cat.display_order === 999);
    const mainCategory =
      final ||
      catsWithPodium.slice().sort((a, b) => {
        return (
          new Date(b.category_date || 0).getTime() -
          new Date(a.category_date || 0).getTime()
        );
      })[0];
    out.push({
      id: t.id,
      tournamentId: t.id,
      tournamentUuid: t.uuid,
      event_date: mainCategory.category_date || t.event_date,
      name: t.name,
      categoryId: mainCategory.id,
      categoryName: catsWithPodium.length === 1 ? mainCategory.name : "",
      categoryCity: catsWithPodium.length === 1 ? mainCategory.city : "",
      categoryDisplayOrder: mainCategory.display_order,
      association: t.association,
      location: t.location,
      description: t.description,
      date_range: t.date_range || "",
      weekend_label: t.weekend_label || "",
      dates: t.dates || [],
      images: t.images || [],
      cover_image: t.cover_image,
      isFinal: !!final,
      categories: catsWithPodium,
    });
  }
  return out;
});

const filteredTorneos = computed(() => {
  const assoc = filterAssociation.value.trim().toLowerCase();
  const cat = filterCategory.value.trim().toLowerCase();
  const date = filterDate.value; // "YYYY-MM-DD" o ""

  return torneos.value.filter((t) => {
    if (assoc && !(t.association || "").toLowerCase().includes(assoc)) {
      return false;
    }
    if (date) {
      const matchesDate =
        normalizeDate(t.event_date) === date ||
        (t.dates || []).some((d) => normalizeDate(d.event_date) === date) ||
        (t.categories || []).some(
          (c) => normalizeDate(c.category_date) === date,
        );
      if (!matchesDate) return false;
    }
    if (cat) {
      const hasCat = (t.categories || []).some((c) =>
        (c.name || "").toLowerCase().includes(cat),
      );
      if (!hasCat) return false;
    }
    return true;
  });
});

// 2. Agrupar items por fin de semana (weekend_label), orden DESC por fecha del item
const groupedTorneos = computed(() => {
  const map = new Map();
  for (const item of flatItems.value) {
    const key = weekendGroupKey(item);
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  // Ordenar cada grupo: primero la "Final" (isFinal), luego por nombre del torneo
  for (const items of map.values()) {
    items.sort((a, b) => {
      if (a.isFinal !== b.isFinal) return a.isFinal ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }
  const groups = Array.from(map.entries()).map(([label, items]) => {
    // date se usa solo para el orden. Lo derivamos del primer item.
    const first = items[0];
    return {
      date: normalizeDate(first.event_date),
      label,
      items,
    };
  });
  groups.sort((a, b) => b.date.localeCompare(a.date));
  return groups;
});

// 3. Paginación sobre los grupos
const totalPages = computed(() =>
  Math.max(1, Math.ceil(groupedTorneos.value.length / GROUPS_PER_PAGE)),
);

const paginatedGroups = computed(() => {
  const start = (currentPage.value - 1) * GROUPS_PER_PAGE;
  return groupedTorneos.value.slice(start, start + GROUPS_PER_PAGE);
});

// 4. Páginas visibles (con elipsis si hay muchas)
const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  if (current <= 4) {
    return [1, 2, 3, 4, 5, "...", total];
  }
  if (current >= total - 3) {
    return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  }
  return [1, "...", current - 1, current, current + 1, "...", total];
});

// Cuándo cambian los filtros → reset a página 1
watch([filterAssociation, filterDate, filterCategory], () => {
  currentPage.value = 1;
});

// === Carga inicial ===
async function load() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarTorneos();
    torneos.value = data.items || [];
    currentPage.value = Math.min(currentPage.value, totalPages.value);
  } catch (_) {
    error.value = "No pudimos cargar los resultados. Intentá nuevamente.";
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>
